import { useEffect, useState } from 'react';
import { api, isValidEik } from '../api.js';

const TYPE_LABEL = { client: 'клиент', supplier: 'доставчик' };

/**
 * Бърз нов контрагент, без да се напуска отворения документ.
 *
 * Пълният картон (адрес, банка, кредитен лимит...) си остава в
 * „Контрагенти" — тук е само каквото трябва, за да тръгне продажбата или
 * доставката веднага: име, и по избор ЕИК, телефон, имейл. Типът се
 * налага от мястото, откъдето е отворен попъпът — продажба праща клиент,
 * доставка праща доставчик — и не се пита, за да няма излишен избор.
 *
 * С `editId` е бърза РЕДАКЦИЯ на избрания контрагент от продажбата/
 * доставката (05.10.2026) — същите полета, записани с PATCH.
 */
export default function QuickPartner({ type, editId = null, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [eik, setEik] = useState('');
  const [mol, setMol] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [city, setCity] = useState('');
  const [address, setAddress] = useState('');
  const [postCode, setPostCode] = useState('');
  const [country, setCountry] = useState('BG');
  const [vatNumber, setVatNumber] = useState('');
  const [isVatRegistered, setIsVatRegistered] = useState(false);
  const [lookup, setLookup] = useState(null);   // { state, message, partner }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [zarezhda, setZarezhda] = useState(!!editId);

  useEffect(() => {
    if (!editId) return;
    api.get(`/api/partners/${editId}`)
      .then((p) => {
        setName(p.name ?? '');
        setEik(p.eik ?? '');
        setMol(p.mol ?? '');
        setPhone(p.phone ?? '');
        setEmail(p.email ?? '');
        setCity(p.city ?? '');
        setAddress(p.address ?? '');
        setPostCode(p.post_code ?? '');
        setCountry(p.country ?? 'BG');
        setVatNumber(p.vat_number ?? '');
        setIsVatRegistered(!!Number(p.is_vat_registered));
      })
      .catch((e) => setError(e.message))
      .finally(() => setZarezhda(false));
  }, [editId]);

  /**
   * Търси по ЕИК: първо в нашата база, после в Търговския регистър.
   * Същата справка като в „Контрагенти" — тук само взима каквото намери
   * и допълва празните полета, без да пипа вече написаното на ръка.
   */
  async function searchByEik() {
    const trimmed = eik.trim();
    if (!trimmed) return;

    // Проверено веднага, без да се чака сървъра — печатна грешка в ЕИК-а
    // няма как да намери фирма, а обикаля през мрежата за нищо.
    if (!isValidEik(trimmed)) {
      setLookup({
        state: 'error',
        message: 'ЕИК-ът не е валиден — трябва да е 9 или 13 цифри с вярна контролна цифра',
      });
      return;
    }

    setLookup({ state: 'searching' });
    try {
      const res = await api.get(`/api/lookup/company/${encodeURIComponent(trimmed)}`
        + (editId ? `?exceptId=${editId}` : ''));

      if (res.duplicate) {
        setLookup({ state: 'duplicate', partner: res.partner });
        return;
      }

      const c = res.company;
      // Редакция — „Опресни“ (23.10.2026): данните от регистъра заместват
      // старите; телефон и поща остават, ако регистърът няма. Записва се с „Запиши“.
      if (editId) {
        const nov = (stari, v) => (v && String(v).trim() ? v : stari);
        setName((n) => nov(n, c.name));
        setPhone((x) => nov(x, c.phone));
        setEmail((x) => nov(x, c.email));
        setMol((x) => nov(x, c.mol));
        setCity((x) => nov(x, c.city));
        setAddress((x) => nov(x, c.address));
        setPostCode((x) => nov(x, c.postCode));
        setCountry((x) => nov(x, c.country));
        setVatNumber((x) => nov(x, c.vatNumber));
        if (c.isVatRegistered) setIsVatRegistered(true);
        setLookup({ state: 'found', message: `Опреснено от: ${c.sources.join(' и ')}. Прегледайте и натиснете „Запиши“.` });
        return;
      }
      setName((n) => n.trim() || c.name || '');
      setPhone((p) => p.trim() || c.phone || '');
      setEmail((e) => e.trim() || c.email || '');
      setMol((v) => v.trim() || c.mol || '');
      setCity((v) => v.trim() || c.city || '');
      setAddress((v) => v.trim() || c.address || '');
      setPostCode((v) => v.trim() || c.postCode || '');
      setCountry((v) => v.trim() || c.country || 'BG');
      setVatNumber((v) => v.trim() || c.vatNumber || '');
      if (c.isVatRegistered) setIsVatRegistered(true);
      setLookup({ state: 'found', message: `Данните са от: ${c.sources.join(' и ')}` });
    } catch (err) {
      setLookup({ state: 'error', message: err.message });
    }
  }

  /** Контрагентът вече съществува — прикачва се направо, вместо да се дублира. */
  function useExisting() {
    onCreated(lookup.partner);
    onClose();
  }

  async function save() {
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      if (editId) {
        const prazno = (v) => (v.trim() === '' ? null : v.trim());
        const updated = await api.patch(`/api/partners/${editId}`, {
          name: name.trim(),
          eik: prazno(eik),
          phone: prazno(phone),
          email: prazno(email),
          mol: prazno(mol),
          city: prazno(city),
          address: prazno(address),
          postCode: prazno(postCode),
          country: prazno(country),
          vatNumber: prazno(vatNumber),
          isVatRegistered,
        });
        onCreated(updated);
        onClose();
        return;
      }
      const created = await api.post('/api/partners', {
        name: name.trim(),
        type,
        eik: eik.trim() || undefined,
        phone: phone.trim() || undefined,
        email: email.trim() || undefined,
        mol: mol.trim() || undefined,
        city: city.trim() || undefined,
        address: address.trim() || undefined,
        postCode: postCode.trim() || undefined,
        country: country.trim() || undefined,
        vatNumber: vatNumber.trim() || undefined,
        isVatRegistered,
      });
      onCreated(created);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onMouseDown={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}>
      <div className="modal" role="dialog" aria-modal="true" style={{ maxWidth: 620 }}>
        <div className="card-head">
          {editId ? `Редакция на ${name || TYPE_LABEL[type] || 'контрагент'}` : `Нов ${TYPE_LABEL[type] ?? 'контрагент'}`}
        </div>
        <div className="card-body">
          {error && <div className="error" style={{ marginBottom: 10 }}>{error}</div>}

          {lookup?.state === 'duplicate' && (
            <div className="error" style={{ marginBottom: 10 }}>
              <strong>Дублиран ЕИК.</strong> Този контрагент вече съществува:{' '}
              {lookup.partner.name}
              {lookup.partner.city ? ` — ${lookup.partner.city}` : ''}
              {!Number(lookup.partner.is_active) && ' (неактивен)'}
              <div style={{ marginTop: 6 }}>
                <button type="button" className="primary" onClick={useExisting}>
                  Използвай този
                </button>
              </div>
            </div>
          )}
          {lookup?.state === 'found' && (
            <div className="notice" style={{ marginBottom: 10 }}>{lookup.message}</div>
          )}
          {lookup?.state === 'error' && (
            <div className="warning" style={{ marginBottom: 10 }}>{lookup.message}</div>
          )}

          <label className="field">
            Наименование
            <input autoFocus value={name} onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') save(); }} />
          </label>
          <label className="field" style={{ marginTop: 10 }}>
            ЕИК
            <div style={{ display: 'flex', gap: 6 }}>
              <input value={eik} style={{ flex: 1 }}
                onChange={(e) => { setEik(e.target.value); setLookup(null); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); searchByEik(); } }} />
              <button type="button" onClick={searchByEik}
                disabled={!eik.trim() || lookup?.state === 'searching'}
                title={editId ? 'Опресни данните от Търговския регистър' : 'Търси в нашата база и в Търговския регистър'}>
                {lookup?.state === 'searching' ? '…' : (editId ? 'Опресни' : 'Търси')}
              </button>
            </div>
          </label>
          <label className="field" style={{ marginTop: 10 }}>
            МОЛ
            <input value={mol} onChange={(e) => setMol(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') save(); }} />
          </label>
          <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
            <label className="field" style={{ flex: 1 }}>
              Телефон
              <input value={phone} onChange={(e) => setPhone(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') save(); }} />
            </label>
            <label className="field" style={{ flex: 1 }}>
              Имейл
              <input value={email} onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') save(); }} />
            </label>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
            <label className="field" style={{ flex: 1 }}>
              Град
              <input value={city} onChange={(e) => setCity(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') save(); }} />
            </label>
            <label className="field" style={{ flex: 2 }}>
              Адрес
              <input value={address} onChange={(e) => setAddress(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') save(); }} />
            </label>
            <label className="field" style={{ width: 90 }}>
              П.к.
              <input value={postCode} onChange={(e) => setPostCode(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') save(); }} />
            </label>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 10, alignItems: 'flex-end' }}>
            <label className="field" style={{ flex: 1 }}>
              ДДС номер
              <input value={vatNumber} onChange={(e) => setVatNumber(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') save(); }} />
            </label>
            <label className="check" style={{ marginBottom: 8 }}>
              <input type="checkbox" checked={isVatRegistered}
                onChange={(e) => setIsVatRegistered(e.target.checked)} />
              Регистриран по ДДС
            </label>
          </div>
          <p className="hint">
            Останалите данни (банка, отстъпка, кредитен лимит) се допълват после
            от „Контрагенти".
          </p>
        </div>
        <div className="modal-foot">
          <button onClick={onClose} disabled={busy}>Откажи</button>
          <button className="primary" disabled={busy || zarezhda || !name.trim()} onClick={save}>
            Запиши
          </button>
        </div>
      </div>
    </div>
  );
}
